<template>
  <v-dialog :value="open" max-width="900" persistent scrollable>
    <v-card>
      <v-card-title class="headline">
        {{ $t('producto.modals.agregarStock.title') }}
      </v-card-title>
      <v-card-text>
        <v-form ref="form" v-model="valid" @submit.prevent="confirm">
          <v-row>
            <v-col cols="12">
              <h3 class="mb-3">{{ $t('producto.modals.agregarStock.productosSeleccionados') }}</h3>
              <v-alert v-if="productosConCantidad.length === 0" type="info" dense text class="mb-3">
                No hay productos seleccionados.
              </v-alert>
              <v-simple-table v-else>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">{{ $t('producto.labels.nombre') }}</th>
                      <th class="text-center">{{ $t('producto.modals.agregarStock.stockDisponible') }}</th>
                      <th class="text-left">{{ $t('producto.modals.agregarStock.cantidadAgregar') }}</th>
                      <th class="text-center" style="width: 56px;"></th>
                      <th class="text-center">{{ $t('producto.modals.agregarStock.nuevoStockFinal') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(producto, index) in productosConCantidad" :key="producto.id || index">
                      <td>
                        <div class="font-weight-medium">
                          {{ producto.nombre || '-' }}
                        </div>
                        <div class="grey--text text--darken-1 text-caption">
                          {{ (producto.marca && producto.marca.nombre) || '' }}
                          <span v-if="producto.modelo && producto.modelo.nombre">
                            - {{ producto.modelo.nombre }}
                          </span>
                          <span v-if="producto.tamanio && producto.tamanio.tamanio">
                            - {{ producto.tamanio.tamanio }}
                          </span>
                        </div>
                      </td>
                      <td class="text-center">
                        <div class="text-center">
                          <v-chip class="text-center" :color="getStockColor(producto.stock)" dark small>
                            {{ producto.stock != null ? producto.stock : 0 }}
                          </v-chip>
                        </div>
                      </td>
                      <td style="max-width: 120px;">
                        <v-text-field
                          v-model.number="producto.cantidadAgregar"
                          type="number"
                          :min="0"
                          :rules="[
                            v => v >= 0 || $t('producto.modals.agregarStock.errorCantidadNegativa')
                          ]"
                          dense
                          outlined
                          hide-details="auto"
                          @input="validateCantidad(producto)"
                        ></v-text-field>
                      </td>
                      <td class="text-center">
                        <v-btn
                          icon
                          small
                          color="primary"
                          @click="incrementOne(producto)"
                          :disabled="loading"
                        >
                          <v-icon small>mdi-plus</v-icon>
                        </v-btn>
                      </td>
                      <td class="text-center">
                        <div class="text-center">
                          <v-chip color="green" dark small>
                            {{ nuevoStockFinal(producto) }}
                          </v-chip>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="close">
          {{ $t('common.cancel') }}
        </v-btn>
        <v-btn
          color="primary"
          @click="confirm"
          :disabled="!tieneCantidadParaAgregar || loading"
          :loading="loading"
        >
          {{ $t('common.confirm') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ProductoProvider from "../../../providers/ProductoProvider";

export default {
  name: "ProductoAgregarStockModal",
  props: {
    open: { type: Boolean, default: false },
    selectedProductos: { type: Array, default: () => [] },
  },
  data() {
    return {
      valid: false,
      loading: false,
      productosConCantidad: [],
      errors: {},
    };
  },
  computed: {
    tieneCantidadParaAgregar() {
      return this.productosConCantidad.some((p) => p.cantidadAgregar > 0);
    },
  },
  watch: {
    open(newVal) {
      if (newVal) {
        this.initialize();
      }
    },
  },
  mounted() {
    if (this.open) {
      this.initialize();
    }
  },
  methods: {
    nuevoStockFinal(producto) {
      const stock = producto.stock != null ? producto.stock : 0;
      const agregar = producto.cantidadAgregar != null && producto.cantidadAgregar >= 0 ? producto.cantidadAgregar : 0;
      return stock + agregar;
    },
    initialize() {
      const selected = this.selectedProductos || [];
      this.productosConCantidad = selected
        .map((producto) => {
          const id =
            producto.id != null
              ? String(producto.id)
              : producto._id != null
                ? String(producto._id)
                : null;
          if (!id) return null;
          return {
            id,
            nombre: producto.nombre,
            marca: producto.marca,
            modelo: producto.modelo,
            tamanio: producto.tamanio,
            stock: producto.stock != null ? Number(producto.stock) : 0,
            cantidadAgregar: 0,
          };
        })
        .filter(Boolean);
    },
    validateCantidad(producto) {
      if (producto.cantidadAgregar == null || producto.cantidadAgregar < 0) {
        this.$set(producto, "cantidadAgregar", 0);
      }
    },
    incrementOne(producto) {
      const actual = producto.cantidadAgregar != null && producto.cantidadAgregar >= 0 ? producto.cantidadAgregar : 0;
      this.$set(producto, "cantidadAgregar", actual + 1);
    },
    getStockColor(stock) {
      if (stock == null || stock === 0) return "red";
      return "green";
    },
    async confirm() {
      if (!this.$refs.form.validate()) {
        return;
      }
      const agregados = this.productosConCantidad
        .filter((p) => p.cantidadAgregar != null && p.cantidadAgregar > 0)
        .map((p) => ({
          productoId: p.id,
          cantidad: p.cantidadAgregar,
        }));
      if (agregados.length === 0) {
        this.$emit("error", this.$t("producto.modals.agregarStock.errorValidacion"));
        return;
      }
      this.loading = true;
      try {
        const response = await ProductoProvider.productoAgregarStock(agregados);
        if (response && response.data && response.data.productoAgregarStock && response.data.productoAgregarStock.ok !== undefined) {
          this.$emit("success", this.$t("producto.modals.agregarStock.success"));
          this.close();
        } else {
          this.$emit("error", this.$t("producto.modals.agregarStock.error"));
        }
      } catch (error) {
        const errorMessage =
          (error && error.graphQLErrors && error.graphQLErrors[0] && error.graphQLErrors[0].message) ||
          (error && error.message) ||
          this.$t("producto.modals.agregarStock.error");
        this.$emit("error", errorMessage);
      } finally {
        this.loading = false;
      }
    },
    close() {
      this.reset();
      this.$emit("close");
    },
    reset() {
      this.productosConCantidad = [];
      this.errors = {};
      this.valid = false;
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
  },
};
</script>

<style scoped></style>
